import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { useControllableState } from '../../hooks/useControllableState';
import { Tooltip } from '../Tooltip/Tooltip';
import './Slider.css';

export type SliderHandleState = 'default' | 'hover' | 'click-on' | 'disabled';

/** Draggable slider thumb with optional value tooltip. */
export type SliderHandleProps = {
  state?: SliderHandleState;
  /** Shown above handle when `state` is hover or click-on. */
  tooltipValue?: string | number;
  className?: string;
  style?: CSSProperties;
  tabIndex?: number;
  role?: string;
  'aria-valuemin'?: number;
  'aria-valuemax'?: number;
  'aria-valuenow'?: number;
  'aria-orientation'?: 'horizontal' | 'vertical';
  'aria-disabled'?: boolean;
  onPointerDown?: (e: PointerEvent<HTMLSpanElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLSpanElement>) => void;
};

export function SliderHandle({
  state = 'default',
  tooltipValue,
  className = '',
  style,
  tabIndex,
  role,
  'aria-valuemin': ariaValueMin,
  'aria-valuemax': ariaValueMax,
  'aria-valuenow': ariaValueNow,
  'aria-orientation': ariaOrientation,
  'aria-disabled': ariaDisabled,
  onPointerDown,
  onKeyDown,
}: SliderHandleProps) {
  const sizeClass =
    state === 'hover'
      ? 'spark-slider-handle-size-hover'
      : state === 'click-on'
        ? 'spark-slider-handle-size-click-on'
        : 'spark-slider-handle-size-default';

  const showTooltip =
    (state === 'hover' || state === 'click-on') && tooltipValue !== undefined;

  const rootClasses = [
    'spark-slider-handle',
    sizeClass,
    state === 'disabled' ? 'spark-slider-handle-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isInteractive = role !== undefined;

  return (
    <span
      className={rootClasses}
      style={style}
      aria-hidden={isInteractive ? undefined : true}
      tabIndex={tabIndex}
      role={role}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      aria-orientation={ariaOrientation}
      aria-disabled={ariaDisabled}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span className="spark-slider-handle-ring" />
      {showTooltip ? (
        <span className="spark-slider-handle-tooltip-wrap">
          <Tooltip
            text={String(tooltipValue)}
            tipDirection="top-center"
            className="spark-slider-handle-tooltip"
          />
        </span>
      ) : null}
    </span>
  );
}

export type SliderType = 'basic' | 'range';

/** Range or basic slider with optional value inputs, tick marks, and tooltip. Controlled and uncontrolled. */
export type SliderProps = {
  /** `'basic'` for a single handle; `'range'` for a from–to pair. */
  type?: SliderType;
  /** Show 0–max scale labels and tick marks under the track. */
  values?: boolean;
  /** Show editable value input(s) beside the track. */
  showInput?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  /** Value granularity. Defaults to 1. */
  step?: number;
  /** Basic slider value (controlled). */
  value?: number;
  /** Basic slider initial value (uncontrolled). */
  defaultValue?: number;
  /** Range slider lower bound (controlled). */
  from?: number;
  /** Range slider initial lower bound (uncontrolled). */
  defaultFrom?: number;
  /** Range slider upper bound (controlled). */
  to?: number;
  /** Range slider initial upper bound (uncontrolled). */
  defaultTo?: number;
  /** Called when the basic slider value changes. */
  onChange?: (value: number) => void;
  /** Called when the range slider values change. */
  onRangeChange?: (from: number, to: number) => void;
  className?: string;
};

function toPercent(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

function snapToStep(raw: number, min: number, max: number, step: number): number {
  const snapped = Math.round((raw - min) / step) * step + min;
  const clamped = Math.min(max, Math.max(min, snapped));
  return Number(clamped.toFixed(10));
}

function SliderInput({
  label,
  displayValue,
  disabled,
  noValuesPadding,
}: {
  label: string;
  displayValue: string | number;
  disabled?: boolean;
  noValuesPadding?: boolean;
}) {
  return (
    <div
      className={[
        'spark-slider-input-wrap',
        noValuesPadding ? 'spark-slider-input-wrap-no-values' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p className="spark-slider-input-label">{label}</p>
      <div className="spark-slider-input-field" aria-hidden="true">
        {displayValue}
      </div>
    </div>
  );
}

function SliderScale({ min, max }: { min: number; max: number }) {
  return (
    <div className="spark-slider-values">
      <p className="spark-slider-values-label">{min}</p>
      <div className="spark-slider-ticks" aria-hidden="true">
        {Array.from({ length: 9 }, (_, i) => (
          <span key={i} className="spark-slider-tick" />
        ))}
      </div>
      <p className="spark-slider-values-label spark-slider-values-label-end">{max}</p>
    </div>
  );
}

const SliderBasic = forwardRef<HTMLDivElement, Omit<SliderProps, 'type' | 'from' | 'to' | 'defaultFrom' | 'defaultTo' | 'onRangeChange'>>(function SliderBasic({
  values = true,
  showInput = true,
  disabled = false,
  min = 0,
  max = 10,
  step = 1,
  value: valueProp,
  defaultValue,
  onChange,
  className = '',
}, ref) {
  const [value, setValue] = useControllableState(valueProp, defaultValue ?? max, onChange);
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const percent = toPercent(value, min, max);
  const handleState: SliderHandleState = disabled
    ? 'disabled'
    : dragging
      ? 'click-on'
      : 'default';

  const positionToValue = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return min;
      const rect = track.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const ratio = rect.width === 0 ? 0 : x / rect.width;
      return snapToStep(min + ratio * (max - min), min, max, step);
    },
    [min, max, step],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      if (disabled) return;
      e.preventDefault();
      const el = e.currentTarget;
      el.setPointerCapture(e.pointerId);
      setDragging(true);
      setValue(positionToValue(e.clientX));

      const onMove = (me: globalThis.PointerEvent) => {
        setValue(positionToValue(me.clientX));
      };
      const onUp = () => {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        setDragging(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [disabled, positionToValue, setValue],
  );

  const valueRef = useRef(value);
  valueRef.current = value;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) return;
      const cur = valueRef.current;
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          next = Math.min(max, cur + step);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          next = Math.max(min, cur - step);
          break;
        case 'Home':
          next = min;
          break;
        case 'End':
          next = max;
          break;
        default:
          return;
      }
      e.preventDefault();
      setValue(snapToStep(next, min, max, step));
    },
    [disabled, min, max, step, setValue],
  );

  const rootClasses = ['spark-slider', disabled ? 'spark-slider-disabled' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} ref={ref}>
      <div
        className={[
          'spark-slider-body',
          !values ? 'spark-slider-body-no-values' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="spark-slider-control" ref={trackRef}>
          <div className="spark-slider-rail" aria-hidden="true" />
          <div
            className="spark-slider-track"
            style={{ width: `${percent}%` }}
            aria-hidden="true"
          />
          <SliderHandle
            state={handleState}
            tooltipValue={value}
            className="spark-slider-handle-pos"
            style={{ left: `${percent}%` }}
            tabIndex={disabled ? undefined : 0}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-orientation="horizontal"
            aria-disabled={disabled || undefined}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
          />
        </div>
        {values ? <SliderScale min={min} max={max} /> : null}
      </div>
      {showInput ? (
        <SliderInput
          label="To"
          displayValue={value}
          disabled={disabled}
          noValuesPadding={!values}
        />
      ) : null}
    </div>
  );
});

const SliderRange = forwardRef<HTMLDivElement, Omit<SliderProps, 'type' | 'value' | 'defaultValue' | 'onChange'>>(function SliderRange({
  values = true,
  showInput = true,
  disabled = false,
  min = 0,
  max = 10,
  step = 1,
  from: fromProp,
  defaultFrom,
  to: toProp,
  defaultTo,
  onRangeChange,
  className = '',
}, ref) {
  const fromRef = useRef(0);
  const toRef = useRef(0);

  const handleFromChange = useCallback(
    (v: number) => {
      onRangeChange?.(v, toRef.current);
    },
    [onRangeChange],
  );
  const handleToChange = useCallback(
    (v: number) => {
      onRangeChange?.(fromRef.current, v);
    },
    [onRangeChange],
  );

  const [fromVal, setFromVal] = useControllableState(
    fromProp,
    defaultFrom ?? min,
    handleFromChange,
  );
  const [toVal, setToVal] = useControllableState(toProp, defaultTo ?? max, handleToChange);

  fromRef.current = fromVal;
  toRef.current = toVal;

  const trackRef = useRef<HTMLDivElement>(null);
  const [draggingFrom, setDraggingFrom] = useState(false);
  const [draggingTo, setDraggingTo] = useState(false);

  const fromPercent = toPercent(fromVal, min, max);
  const toPercentValue = toPercent(toVal, min, max);
  const fromHandleState: SliderHandleState = disabled
    ? 'disabled'
    : draggingFrom
      ? 'click-on'
      : 'default';
  const toHandleState: SliderHandleState = disabled
    ? 'disabled'
    : draggingTo
      ? 'click-on'
      : 'default';

  const positionToValue = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return min;
      const rect = track.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const ratio = rect.width === 0 ? 0 : x / rect.width;
      return snapToStep(min + ratio * (max - min), min, max, step);
    },
    [min, max, step],
  );

  const handleFromPointerDown = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      if (disabled) return;
      e.preventDefault();
      const el = e.currentTarget;
      el.setPointerCapture(e.pointerId);
      setDraggingFrom(true);

      const clamp = (v: number) => Math.min(v, toRef.current);
      setFromVal(clamp(positionToValue(e.clientX)));

      const onMove = (me: globalThis.PointerEvent) => {
        setFromVal(clamp(positionToValue(me.clientX)));
      };
      const onUp = () => {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        setDraggingFrom(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [disabled, positionToValue, setFromVal],
  );

  const handleToPointerDown = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      if (disabled) return;
      e.preventDefault();
      const el = e.currentTarget;
      el.setPointerCapture(e.pointerId);
      setDraggingTo(true);

      const clamp = (v: number) => Math.max(v, fromRef.current);
      setToVal(clamp(positionToValue(e.clientX)));

      const onMove = (me: globalThis.PointerEvent) => {
        setToVal(clamp(positionToValue(me.clientX)));
      };
      const onUp = () => {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        setDraggingTo(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [disabled, positionToValue, setToVal],
  );

  const handleFromKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) return;
      const cur = fromRef.current;
      const hi = toRef.current;
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          next = Math.min(hi, cur + step);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          next = Math.max(min, cur - step);
          break;
        case 'Home':
          next = min;
          break;
        case 'End':
          next = hi;
          break;
        default:
          return;
      }
      e.preventDefault();
      setFromVal(snapToStep(next, min, max, step));
    },
    [disabled, min, max, step, setFromVal],
  );

  const handleToKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) return;
      const cur = toRef.current;
      const lo = fromRef.current;
      let next: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          next = Math.min(max, cur + step);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          next = Math.max(lo, cur - step);
          break;
        case 'Home':
          next = lo;
          break;
        case 'End':
          next = max;
          break;
        default:
          return;
      }
      e.preventDefault();
      setToVal(snapToStep(next, min, max, step));
    },
    [disabled, min, max, step, setToVal],
  );

  const rootClasses = [
    'spark-slider-range-wrap',
    disabled ? 'spark-slider-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} ref={ref}>
      {showInput ? (
        <SliderInput
          label="From"
          displayValue={fromVal}
          disabled={disabled}
          noValuesPadding={!values}
        />
      ) : null}
      <div
        className={[
          'spark-slider-range-body',
          !values ? 'spark-slider-range-body-no-values' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="spark-slider-control" ref={trackRef}>
          <div className="spark-slider-rail spark-slider-rail-range" aria-hidden="true" />
          <div
            className="spark-slider-range-active"
            style={{ left: `${fromPercent}%`, width: `${toPercentValue - fromPercent}%` }}
            aria-hidden="true"
          />
          <SliderHandle
            state={fromHandleState}
            tooltipValue={fromVal}
            className="spark-slider-handle-pos"
            style={{ left: `${fromPercent}%` }}
            tabIndex={disabled ? undefined : 0}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={fromVal}
            aria-orientation="horizontal"
            aria-disabled={disabled || undefined}
            onPointerDown={handleFromPointerDown}
            onKeyDown={handleFromKeyDown}
          />
          <SliderHandle
            state={toHandleState}
            tooltipValue={toVal}
            className="spark-slider-handle-pos"
            style={{ left: `${toPercentValue}%` }}
            tabIndex={disabled ? undefined : 0}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={toVal}
            aria-orientation="horizontal"
            aria-disabled={disabled || undefined}
            onPointerDown={handleToPointerDown}
            onKeyDown={handleToKeyDown}
          />
        </div>
        {values ? <SliderScale min={min} max={max} /> : null}
      </div>
      {showInput ? (
        <SliderInput
          label="To"
          displayValue={toVal}
          disabled={disabled}
          noValuesPadding={!values}
        />
      ) : null}
    </div>
  );
});

export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider({
  type = 'basic',
  values = true,
  showInput = true,
  disabled = false,
  min = 0,
  max = 10,
  step = 1,
  value,
  defaultValue,
  from,
  defaultFrom,
  to,
  defaultTo,
  onChange,
  onRangeChange,
  className = '',
}, ref) {
  if (type === 'range') {
    return (
      <SliderRange
        ref={ref}
        values={values}
        showInput={showInput}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        from={from}
        defaultFrom={defaultFrom}
        to={to}
        defaultTo={defaultTo}
        onRangeChange={onRangeChange}
        className={className}
      />
    );
  }

  return (
    <SliderBasic
      ref={ref}
      values={values}
      showInput={showInput}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      className={className}
    />
  );
});

Slider.displayName = 'Slider';
